import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdSense from '../../components/shared/AdSense';
import sportsKeywords from './sportsKeywords';
import SuggestionModal from './SuggestionModal';
import {
  GuessPlayerIconNew,
  MatchVoteIconNew,
  DailyQuizIconNew,
  FlagClashIconNew,
  DrawGameIconNew,
  InfinityCraftIconNew,
  SportsGamesIconNew,
  SportsMarketIconNew,
  SalesSimulatorIconNew,
  PlayerReviewIconNew,
  OnThisDayIconNew,
  FunDilemmaIconNew,
  FloatingSportsBalls,
  SuggestionBulbIcon,
  FooterTrophyIcon,
  FooterSportsBallsRow
} from './homePageIconsNew';

const HomePage = () => {
  const navigate = useNavigate();
  const [suggestionEmail, setSuggestionEmail] = useState('');
  const [suggestionCategory, setSuggestionCategory] = useState('');
  const [suggestionMessage, setSuggestionMessage] = useState('');
  const [suggestionStatus, setSuggestionStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const resetSuggestionForm = useCallback(() => {
    setSuggestionEmail('');
    setSuggestionCategory('');
    setSuggestionMessage('');
    setSuggestionStatus('');
  }, []);

  const openSuggestionModal = useCallback(() => {
    setSuggestionStatus('');
    setShowSuggestionForm(true);
  }, []);

  const closeSuggestionModal = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setShowSuggestionForm(false);
    resetSuggestionForm();
  }, [isSubmitting, resetSuggestionForm]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (next) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return next;
    });
  };

  // Ensure proper mobile viewport
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=5.0');
    }
  }, []);

  const [adstraSettings, setAdstraSettings] = useState({
    adstraLink: '',
    impressionsRequired: 0,
    isEnabled: false
  });
  const [adstraClickCount, setAdstraClickCount] = useState(() => {
    try {
      const saved = sessionStorage.getItem('adstra_click_count');
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });

  useEffect(() => {
    const fetchAdstraSettings = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/adstra/settings`);
        if (response.ok) {
          const data = await response.json();
          setAdstraSettings(data);
        }
      } catch (error) {
        console.error('Error fetching Adstra settings:', error);
      }
    };
    fetchAdstraSettings();
  }, [apiUrl]);

  useEffect(() => {
    if (!showSuggestionForm) {
      return undefined;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showSuggestionForm]);

  useEffect(() => {
    const prefetchSportsData = async () => {
      const today = new Date();
      const datesToFetch = [];
      for (let i = 0; i < 8; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        datesToFetch.push(date);
      }
      const fetchPromises = datesToFetch.map(date =>
        prefetchDataForDate(date).catch(err => {
          console.error(`Failed to prefetch data for ${date.toDateString()}:`, err);
          return null;
        })
      );
      await Promise.allSettled(fetchPromises);
    };

    const prefetchTimer = setTimeout(() => {
      prefetchSportsData();
    }, 1000);

    return () => clearTimeout(prefetchTimer);
  }, []);

  const prefetchDataForDate = async (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const cacheKey = `sports_ultra_${month}_${day}`;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const cachedData = JSON.parse(cached);
        if (Date.now() - cachedData.timestamp < 3600000) {
          return;
        }
      }
    } catch (error) {
      console.error('Cache check error:', error);
    }

    try {
      const apiPromises = [
        fetchWikipediaEnhanced(month, day),
        fetchMuffinLabsData(month, day),
        fetchDayInHistoryData(month, day)
      ];
      const results = await Promise.allSettled(apiPromises);
      const allData = results
        .filter(r => r.status === 'fulfilled' && r.value)
        .map(r => r.value);
      const mergedData = mergeMultipleApiData(allData);

      if (mergedData.events.length > 0 || mergedData.births.length > 0) {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data: mergedData,
          timestamp: Date.now()
        }));
      }
    } catch (error) {
      console.error(`Error prefetching data for ${month}/${day}:`, error);
    }
  };

  const fetchWikipediaEnhanced = async (month, day) => {
    try {
      const response = await fetch(`https://en.wikipedia.org/api/rest_v1/feed/onthisday/all/${month}/${day}`);
      if (!response.ok) return null;
      const data = await response.json();
      return filterSportsContent({
        events: (data.events || []).map(i => ({ text: i.text, year: i.year, pages: i.pages || [] })),
        births: (data.births || []).map(i => ({ text: i.text, year: i.year, pages: i.pages || [] })),
        deaths: (data.deaths || []).map(i => ({ text: i.text, year: i.year, pages: i.pages || [] }))
      });
    } catch (error) {
      return null;
    }
  };

  const fetchMuffinLabsData = async (month, day) => {
    try {
      const response = await fetch(`https://history.muffinlabs.com/date/${month}/${day}`);
      if (!response.ok) return null;
      const data = await response.json();
      if (!data.data) return null;
      return filterSportsContent({
        events: (data.data.Events || []).map(i => ({ text: i.text, year: parseInt(i.year) })),
        births: (data.data.Births || []).map(i => ({ text: i.text, year: parseInt(i.year) })),
        deaths: (data.data.Deaths || []).map(i => ({ text: i.text, year: parseInt(i.year) }))
      });
    } catch (error) {
      return null;
    }
  };

  const fetchDayInHistoryData = async (month, day) => {
    try {
      const response = await fetch(`https://dayinhistory.dev/api/events/${month}/${day}`);
      if (!response.ok) return null;
      const data = await response.json();
      return filterSportsContent({
        events: (data.events || []).map(i => ({ text: i.description || i.text, year: i.year })),
        births: (data.births || []).map(i => ({ text: i.description || i.text, year: i.year })),
        deaths: (data.deaths || []).map(i => ({ text: i.description || i.text, year: i.year }))
      });
    } catch (error) {
      return null;
    }
  };

  const filterSportsContent = (data) => {
    const isSportsRelated = (text) => {
      if (!text) return false;
      const lower = text.toLowerCase();
      return sportsKeywords.some(keyword => lower.includes(keyword.toLowerCase()));
    };
    const filterItems = (items) => (items || []).filter(i => i.text && isSportsRelated(i.text));
    return {
      events: filterItems(data.events),
      births: filterItems(data.births),
      deaths: filterItems(data.deaths)
    };
  };

  const mergeMultipleApiData = (dataArray) => {
    const merged = { events: [], births: [], deaths: [] };
    dataArray.forEach(data => {
      if (data) {
        merged.events.push(...(data.events || []));
        merged.births.push(...(data.births || []));
        merged.deaths.push(...(data.deaths || []));
      }
    });
    return merged;
  };

  const gameCards = [
    {
      id: 'guess-player',
      title: 'Guess Player',
      description: 'Guess the mystery legend',
      icon: <GuessPlayerIconNew isDarkMode={isDarkMode} />,
      accentColor: 'bg-blue-500',
      route: '/mystery-sports-legend'
    },
    {
      id: 'match-vote',
      title: 'Match Vote',
      description: "Vote on today's matches",
      icon: <MatchVoteIconNew isDarkMode={isDarkMode} />,
      accentColor: 'bg-emerald-500',
      route: '/match-vote'
    },
    {
      id: 'daily-quiz',
      title: 'Daily Quiz',
      description: 'Test your sports knowledge',
      icon: <DailyQuizIconNew isDarkMode={isDarkMode} />,
      accentColor: 'bg-purple-600',
      route: '/daily-quiz'
    },
    {
      id: 'flag-clash',
      title: 'Flag Clash',
      description: 'Battle flag vs flag and win',
      icon: <FlagClashIconNew isDarkMode={isDarkMode} />,
      accentColor: 'bg-red-500',
      route: '/country-battle'
    },
    {
      id: 'draw-game',
      title: 'Draw Game',
      description: 'Draw sports from memory',
      icon: <DrawGameIconNew isDarkMode={isDarkMode} />,
      accentColor: 'bg-orange-500',
      route: '/draw-game'
    },
    {
      id: 'infinity-craft',
      title: 'Infinity Craft',
      description: 'Mix & discover sports items',
      icon: <InfinityCraftIconNew isDarkMode={isDarkMode} />,
      accentColor: 'bg-purple-500',
      route: '/sports-craft'
    },
    {
      id: 'sports-games',
      title: 'Sports Games',
      description: '1000+ fun mini games',
      icon: <SportsGamesIconNew isDarkMode={isDarkMode} />,
      accentColor: 'bg-blue-600',
      route: '/sports-games'
    },
    {
      id: 'sports-market',
      title: 'Sports Market',
      description: 'Buy & sell sports items',
      icon: <SportsMarketIconNew isDarkMode={isDarkMode} />,
      accentColor: 'bg-amber-500',
      route: '/buy-sell'
    },
    {
      id: 'sales-simulator',
      title: 'Sales Simulator',
      description: 'Run your virtual sports business',
      icon: <SalesSimulatorIconNew />,
      accentColor: 'bg-emerald-500',
      route: '/sell-sell-sell'
    },
    {
      id: 'player-review',
      title: 'Player Review',
      description: 'Rate & review your favorite players',
      icon: <PlayerReviewIconNew />,
      accentColor: 'bg-red-500',
      route: '/player-review'
    },
    {
      id: 'on-this-day',
      title: 'On This Day',
      description: 'On this day in sports history',
      icon: <OnThisDayIconNew isDarkMode={isDarkMode} />,
      accentColor: 'bg-amber-700',
      route: '/on-this-day'
    },
    {
      id: 'fun-dilemma',
      title: 'Fun Dilemma',
      description: '5 daily dilemmas • Build your streak!',
      icon: <FunDilemmaIconNew />,
      accentColor: 'bg-rose-500',
      route: '/fun-dilemma'
    }
  ];

  const handleSuggestionSubmit = async (e) => {
    e.preventDefault();
    if (!suggestionEmail || !suggestionCategory || !suggestionMessage) {
      setSuggestionStatus('error');
      setTimeout(() => setSuggestionStatus(''), 3000);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(suggestionEmail)) {
      setSuggestionStatus('invalid-email');
      setTimeout(() => setSuggestionStatus(''), 3000);
      return;
    }
    setIsSubmitting(true);
    setSuggestionStatus('sending');
    try {
      const response = await fetch(`${apiUrl}/api/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: suggestionEmail,
          category: suggestionCategory,
          message: suggestionMessage
        })
      });
      if (response.ok) {
        setSuggestionStatus('success');
        resetSuggestionForm();
        setTimeout(() => {
          setShowSuggestionForm(false);
          setSuggestionStatus('');
        }, 2000);
      } else {
        setSuggestionStatus('failed');
        setTimeout(() => setSuggestionStatus(''), 3000);
      }
    } catch (error) {
      setSuggestionStatus('network-error');
      setTimeout(() => setSuggestionStatus(''), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCardClick = useCallback((item) => {
    if (!item.route) return;
    if (adstraSettings.isEnabled && adstraSettings.adstraLink && adstraSettings.impressionsRequired > 0) {
      if (adstraClickCount < adstraSettings.impressionsRequired) {
        const newCount = adstraClickCount + 1;
        setAdstraClickCount(newCount);
        try {
          sessionStorage.setItem('adstra_click_count', newCount.toString());
          sessionStorage.setItem('adstra_pending_route', item.route);
        } catch (e) {
          console.error(e);
        }
        fetch(`${apiUrl}/api/adstra/impression`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }).catch(err => console.error(err));
        if (newCount >= adstraSettings.impressionsRequired) {
          fetch(`${apiUrl}/api/adstra/completed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          }).catch(err => console.error(err));
        }
        window.location.href = adstraSettings.adstraLink;
        return;
      }
    }
    navigate(item.route);
  }, [navigate, adstraSettings, adstraClickCount, apiUrl]);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-[#0B0F19] text-white' : 'bg-[#FAFCFF] text-slate-900'}`}>
      {showSuggestionForm && (
        <SuggestionModal
          suggestionEmail={suggestionEmail}
          setSuggestionEmail={setSuggestionEmail}
          suggestionCategory={suggestionCategory}
          setSuggestionCategory={setSuggestionCategory}
          suggestionMessage={suggestionMessage}
          setSuggestionMessage={setSuggestionMessage}
          suggestionStatus={suggestionStatus}
          isSubmitting={isSubmitting}
          onClose={closeSuggestionModal}
          onSubmit={handleSuggestionSubmit}
        />
      )}

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">

        {/* TOP BAR / HEADER */}
        <header className="flex items-center justify-between py-3 sm:py-4 mb-4 sm:mb-6 border-b border-slate-200/80 dark:border-slate-800">
          <div className="flex items-center gap-1 cursor-pointer" onClick={() => navigate('/')}>
            <span className={`font-black text-xl sm:text-2xl tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              I LOVE SPORTS
            </span>
            <span className="font-black text-xl sm:text-2xl text-[#0066FF] tracking-tight">
              .FUN
            </span>
          </div>

          <button
            onClick={toggleDarkMode}
            aria-label="Toggle Theme"
            className="relative w-14 h-7 sm:w-16 sm:h-8 rounded-full p-1 transition-colors duration-300 focus:outline-none flex items-center justify-between shadow-inner"
            style={{ backgroundColor: isDarkMode ? '#1E293B' : '#E2E8F0', border: '1px solid rgba(0,0,0,0.1)' }}
          >
            <span className={`text-xs sm:text-sm transform transition-transform duration-300 ${isDarkMode ? 'opacity-40 scale-75' : 'opacity-100 scale-100 text-amber-500'}`}>
              ☀️
            </span>
            <span className={`text-xs sm:text-sm transform transition-transform duration-300 ${isDarkMode ? 'opacity-100 scale-100 text-indigo-300' : 'opacity-40 scale-75'}`}>
              🌙
            </span>
            <div
              className={`absolute top-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white shadow-md transform transition-transform duration-300 ${isDarkMode ? 'translate-x-7 sm:translate-x-8 bg-slate-900' : 'translate-x-0'}`}
            />
          </button>
        </header>

        {/* HERO SECTION */}
        <section className="relative text-center pt-2 sm:pt-4 pb-14 sm:pb-10 overflow-hidden">
          {/* Floating Sports Balls - Only shown on medium+ screens so they never overlap mobile text! */}
          <FloatingSportsBalls isDarkMode={isDarkMode} />

          <div className="relative z-10 max-w-4xl mx-auto px-2">
            <h1 className="text-[28px] sm:text-5xl md:text-6xl lg:text-[4rem] font-extrabold tracking-tight leading-[1.05]">
              <span className="text-[#0066FF]">Play </span>
              <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>the World </span>
              <br />
              <span className="text-[#7C3AED]">of Sports</span>
            </h1>

            <p className={`mt-3 sm:mt-4 text-xs sm:text-lg md:text-xl font-semibold flex items-center justify-center flex-wrap gap-1 sm:gap-2 px-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              <span>Play</span>
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block mx-1"></span>
              <span>Vote</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block mx-1"></span>
              <span>Explore</span>
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block mx-1"></span>
              <span>Fun with Sports.</span>
            </p>
          </div>
        </section>

        {/* AdSense Top Banner */}
        <AdSense slot="homePageTop" className="mb-6 sm:mb-8" />

        {/* CATEGORY & GAMES GRID (Responsive 2-column layout on mobile, 4-column on desktop) */}
        <main className="mb-8 sm:mb-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {gameCards.map(card => (
              <div
                key={card.id}
                onClick={() => handleCardClick(card)}
                className={`
                  group
                  rounded-2xl
                  sm:rounded-3xl
                  p-3.5
                  sm:p-6
                  ${isDarkMode ? 'bg-slate-800/90 border-slate-700/80 hover:bg-slate-800' : 'bg-white border-slate-200/80 hover:bg-white'}
                  border
                  shadow-sm
                  hover:shadow-xl
                  hover:-translate-y-1.5
                  transition-all
                  duration-300
                  flex
                  flex-col
                  items-center
                  justify-between
                  text-center
                  cursor-pointer
                  relative
                  overflow-hidden
                `}
                style={{ minHeight: '190px' }}
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="h-20 sm:h-28 mb-2 sm:mb-3 flex items-center justify-center transform group-hover:scale-[1.04] transition-transform duration-300">
                  {card.icon}
                </div>

                <div className="flex-1 flex flex-col justify-center">
                  <h3 className={`font-extrabold text-[13px] sm:text-lg tracking-tight transition-colors duration-200 ${isDarkMode ? 'text-white group-hover:text-blue-400' : 'text-slate-900 group-hover:text-[#0066FF]'}`}>
                    {card.title}
                  </h3>
                  <p className={`text-[11px] sm:text-sm mt-1 sm:mt-1.5 leading-tight sm:leading-snug font-semibold max-w-[150px] sm:max-w-[200px] mx-auto ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {card.description}
                  </p>
                </div>

                <div className={`w-8 sm:w-10 h-1 sm:h-1.5 rounded-full ${card.accentColor} mt-2.5 sm:mt-4 group-hover:w-16 transition-all duration-300`} />
              </div>
            ))}
          </div>
        </main>

        {/* AdSense Bottom Banner */}
        <AdSense slot="homePageBottom" className="mb-6 sm:mb-8" />

        {/* CATEGORY SUGGESTION BANNER */}
        <section className="mb-8 sm:mb-10">
          <div
            className={`
              rounded-2xl sm:rounded-[28px]
              p-4 sm:p-6
              flex
              flex-col
              sm:flex-row
              items-center
              justify-between
              gap-3 sm:gap-4
              relative
              overflow-hidden
              shadow-[0_2px_12px_rgba(0,0,0,0.03)]
              border
              ${isDarkMode ? 'bg-slate-800/90 border-slate-700' : 'bg-[#F4F6FD] border-[#E2E8F0]'}
            `}
          >
            {/* Left Content */}
            <div className="flex items-center gap-3 sm:gap-4 text-center sm:text-left relative z-10">
              <SuggestionBulbIcon />
              <div>
                <h2 className={`font-extrabold text-base sm:text-xl tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Have an idea for the next category?
                </h2>
                <p className={`text-xs sm:text-base font-semibold mt-0.5 ${isDarkMode ? 'text-slate-200' : 'text-slate-600'}`}>
                  We'd love to hear from you!
                </p>
              </div>
            </div>

            {/* Connecting dashed squiggly vector path (hidden on mobile) */}
            <svg viewBox="0 0 120 40" className="hidden lg:block absolute right-56 bottom-3 w-28 h-10 pointer-events-none text-blue-400 opacity-60">
              <path d="M5,25 Q30,35 50,18 T75,22 Q90,5 95,25 T115,20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" />
            </svg>

            {/* Send Suggestion Button */}
            <button
              onClick={openSuggestionModal}
              className="
                relative
                z-10
                w-full
                sm:w-auto
                bg-[#0066FF]
                hover:bg-[#0052CC]
                active:scale-95
                text-white
                font-bold
                px-5
                sm:px-6
                py-2.5
                sm:py-3
                rounded-full
                flex
                items-center
                justify-center
                gap-2
                shadow-sm
                transition-all
                duration-200
                text-xs
                sm:text-base
                flex-shrink-0
              "
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 fill-current transform rotate-[-20deg]" viewBox="0 0 24 24">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
              <span>Send Suggestion</span>
            </button>
          </div>
        </section>

        {/* FOOTER CARD & COPYRIGHT SECTION */}
        <footer className="pb-8 sm:pb-10">
          <div
            className={`
              rounded-2xl sm:rounded-[28px]
              p-4 sm:p-6
              flex
              flex-col
              sm:flex-row
              items-center
              gap-4 sm:gap-6
              shadow-[0_2px_12px_rgba(0,0,0,0.03)]
              border
              ${isDarkMode ? 'bg-slate-800/90 border-slate-700' : 'bg-[#F4F6FD] border-[#E2E8F0]'}
            `}
          >
            <FooterTrophyIcon />

            <div className="text-center sm:text-left">
              <h3 className={`font-extrabold text-sm sm:text-lg tracking-tight flex items-center justify-center sm:justify-start gap-1.5 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Made with <span className="text-red-500">❤️</span> for the World of Sports
              </h3>

              <p className={`text-xs sm:text-base font-semibold mt-0.5 ${isDarkMode ? 'text-slate-200' : 'text-slate-600'}`}>
                Join sports fans discovering <span className="text-[#0066FF] font-bold">fun sports facts</span> every day
              </p>

              <FooterSportsBallsRow />
            </div>
          </div>

          <p className={`text-center text-xs sm:text-sm font-semibold mt-4 sm:mt-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            © 2025 I Love Sports.Fun • All rights reserved
          </p>
        </footer>

      </div>
    </div>
  );
};

export default HomePage;
